import { useEffect } from 'react';
import { Alert, App, Button, Drawer, Form, Input, Switch, Tag } from 'antd';
import { DataTable } from '../../components/DataTable';
import type { ColumnsType } from 'antd/es/table';
import { Info } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ErrorState, LoadingState } from '../../components/PageStates';
import { catalogApi, catalogKeys, type Branch, type PosStoreCode } from './api';
import { tokens } from '../../theme';

interface FormValues {
  storeCode: string;
  label?: string;
}

/**
 * Khai mã cửa hàng POS cho một chi nhánh (`S3-Web-02`, ADR-008).
 *
 * Đây là danh sách trắng của **lớp (a) BR-215** — lớp duy nhất trong bốn lớp được phép CHẶN. Nó thay
 * chỗ danh sách trắng số tài khoản (BR-202) đã mất khi QR bỏ VietQR: mã QR mới không có số tài khoản,
 * nên mã cửa hàng vừa suy ra chi nhánh vừa chặn hóa đơn của quán khác.
 */
export function PosStoreCodesDrawer({
  branch,
  onClose,
}: {
  branch: Branch | null;
  onClose: () => void;
}) {
  const [form] = Form.useForm<FormValues>();
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  const open = branch !== null;

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const codesQuery = useQuery({
    queryKey: catalogKeys.posStoreCodes,
    queryFn: catalogApi.listPosStoreCodes,
    enabled: open,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: catalogKeys.posStoreCodes });

  const addMutation = useMutation({
    mutationFn: (values: FormValues) =>
      catalogApi.addPosStoreCode({
        branchId: branch?.id ?? '',
        storeCode: values.storeCode.trim(),
        label: values.label?.trim() || null,
      }),
    onSuccess: async () => {
      await invalidate();
      form.resetFields();
      message.success('Đã khai mã cửa hàng. Hóa đơn mang mã này tích điểm được ngay.');
    },
    onError: (error: Error) => message.error(error.message),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      catalogApi.setPosStoreCodeActive(id, isActive),
    onSuccess: async (code) => {
      await invalidate();
      message.success(
        code.isActive
          ? `Đã bật lại mã ${code.storeCode}.`
          : `Đã tắt mã ${code.storeCode}. Hóa đơn mang mã này KHÔNG tích điểm được nữa.`,
      );
    },
    onError: (error: Error) => message.error(error.message),
  });

  // Chỉ hiện mã của chi nhánh đang mở. Gọi một lần cho toàn hệ thống rồi lọc ở client vì bảng này
  // chỉ có vài dòng mỗi chi nhánh — và cách đó cho phép cảnh báo khi mã đã thuộc chi nhánh khác.
  const rows = (codesQuery.data ?? []).filter((x) => x.branchId === branch?.id);

  const columns: ColumnsType<PosStoreCode> = [
    {
      title: 'Mã cửa hàng',
      dataIndex: 'storeCode',
      render: (value: string) => <strong style={{ fontFamily: 'monospace' }}>{value}</strong>,
    },
    {
      title: 'Ghi chú',
      dataIndex: 'label',
      render: (value: string | null | undefined) => value || '—',
    },
    {
      title: 'Đang dùng',
      dataIndex: 'isActive',
      width: 120,
      render: (isActive: boolean, row) => (
        <Switch
          checked={isActive}
          loading={toggleMutation.isPending}
          onChange={(next) => toggleMutation.mutate({ id: row.id, isActive: next })}
          aria-label={`Bật tắt mã ${row.storeCode}`}
        />
      ),
    },
  ];

  return (
    <Drawer
      title={`Mã cửa hàng POS — ${branch?.name ?? ''}`}
      open={open}
      onClose={onClose}
      width={620}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        icon={<Info size={18} />}
        style={{ marginBottom: tokens.space.sectionGap }}
        message="Mã in ở đầu mã QR trên phiếu thanh toán"
        description={
          <span>
            Ví dụ <code>POS002</code>. Chưa khai thì hóa đơn của chi nhánh này{' '}
            <strong>không tích điểm được</strong> — hệ thống không biết mã đó thuộc đâu. Một chi
            nhánh nhiều máy POS thì khai nhiều mã.
          </span>
        }
      />

      <Form form={form} layout="vertical" onFinish={(values) => addMutation.mutate(values)}>
        <Form.Item
          name="storeCode"
          label="Mã cửa hàng"
          rules={[
            { required: true, message: 'Nhập mã cửa hàng in trên hóa đơn.' },
            {
              pattern: /^[A-Za-z0-9_-]+$/,
              message: 'Chỉ gồm chữ, số, gạch ngang và gạch dưới — không có khoảng trắng.',
            },
          ]}
        >
          <Input placeholder="POS002" maxLength={32} style={{ fontFamily: 'monospace' }} />
        </Form.Item>

        <Form.Item name="label" label="Ghi chú (không bắt buộc)">
          <Input placeholder="Quầy 1 — tầng 2" maxLength={200} />
        </Form.Item>

        <Button type="primary" htmlType="submit" loading={addMutation.isPending}>
          Thêm mã
        </Button>
      </Form>

      <div style={{ marginTop: tokens.space.sectionGap }}>
        {codesQuery.isPending ? (
          <LoadingState />
        ) : codesQuery.isError ? (
          <ErrorState error={codesQuery.error} onRetry={() => codesQuery.refetch()} />
        ) : rows.length === 0 ? (
          <Alert
            type="warning"
            showIcon
            message="Chi nhánh này chưa khai mã cửa hàng nào"
            description="Hóa đơn định dạng mới của chi nhánh này sẽ bị từ chối tới khi có ít nhất một mã."
          />
        ) : (
          <>
            <DataTable toolbar={false}
              rowKey="id"
              size="small"
              pagination={false}
              columns={columns}
              dataSource={rows}
            />
            {rows.every((x) => !x.isActive) && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: tokens.space.cardPadding }}
                message="Mọi mã của chi nhánh này đang tắt"
                description="Hóa đơn của chi nhánh sẽ bị từ chối cho tới khi bật lại một mã."
              />
            )}
          </>
        )}
      </div>

      <div style={{ marginTop: tokens.space.sectionGap, color: tokens.color.ink3, fontSize: 12.5 }}>
        Tắt thay vì xóa: hóa đơn cũ đã tích điểm vẫn tham chiếu mã này khi đối soát.{' '}
        <Tag color="default">Mọi thay đổi đều ghi nhật ký</Tag>
      </div>
    </Drawer>
  );
}
